import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { getOwedDeliveryPayables, payDeliveryPayable } from '../api';

// delivery_person_payables.md - what a rental owner owes delivery people / agents per completed
// leg. The owner pays them directly (there's no gateway) and records it here.
const STATUS_VARIANT = { pending: 'warning', escalated: 'danger', paid: 'success', rejected: 'secondary' };
const LEG_LABEL = { OUTBOUND: 'Delivery', RETURN: 'Return', VISIT: 'Visit' };
const METHODS = ['UPI', 'BANK_TRANSFER', 'CASH', 'CARD', 'NET_BANKING', 'WALLET', 'OTHER'];
const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function PayModal({ payable, show, onHide, onPaid }) {
  const [method, setMethod] = useState('UPI');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setMethod('UPI'); setReference(''); setNote(''); setError('');
  }, [show]);

  if (!payable) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (reference.trim().length < 2) { setError('Enter the payment reference / transaction id.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await payDeliveryPayable(payable._id, { method, reference: reference.trim(), note: note.trim() });
      onPaid();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to record this payment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Record payment to {payable.deliveryPersonName || 'the delivery person'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            Order #{payable.bookingRef} &middot; {LEG_LABEL[payable.leg] || payable.leg} &middot; <strong>{rupees(payable.amount)}</strong>
          </p>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          <Form.Group className="mb-2">
            <Form.Label>Method</Form.Label>
            <Form.Select value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Reference / transaction id <span className="text-danger">*</span></Form.Label>
            <Form.Control required minLength={2} maxLength={200} value={reference} onChange={(e) => setReference(e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
          <Button variant="success" type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Confirm paid'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default function OwnerPayablesPanel() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payFor, setPayFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await getOwedDeliveryPayables(statusFilter === 'all' ? undefined : statusFilter));
    } catch {
      setError('Could not load delivery payments.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const rowsPaged = usePagedArray(rows, { resetKey: statusFilter });

  if (loading) return <Spinner animation="border" role="status" />;

  const outstanding = rows.filter((p) => ['pending', 'escalated'].includes(p.status)).reduce((s, p) => s + p.amount, 0);

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <Alert variant="light" className="fw-semibold mb-0">You owe delivery people: {rupees(outstanding)}</Alert>
        <Form.Select size="sm" style={{ maxWidth: 160 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="escalated">Escalated</option>
          <option value="paid">Paid</option>
        </Form.Select>
      </div>

      {rowsPaged.total === 0 ? (
        <Alert variant="light">No delivery payments yet.</Alert>
      ) : (
        <>
        <Table responsive hover>
          <thead>
            <tr><th>Order</th><th>Delivery person</th><th>For</th><th>Amount</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rowsPaged.rows.map((p) => (
              <tr key={p._id} className={p.status === 'escalated' ? 'table-warning' : undefined}>
                <td className="font-monospace">#{p.bookingRef}</td>
                <td>
                  {p.deliveryPersonName || '—'}
                  {p.deliveryPersonPhone && <div className="small text-muted">{p.deliveryPersonPhone}</div>}
                </td>
                <td>{LEG_LABEL[p.leg] || p.leg}</td>
                <td>{rupees(p.amount)}</td>
                <td>
                  <Badge bg={STATUS_VARIANT[p.status] || 'secondary'}>{p.status}</Badge>
                  {p.status === 'escalated' && <div className="small text-danger">Escalated to the website owner</div>}
                </td>
                <td>
                  {['pending', 'escalated'].includes(p.status) && (
                    <Button size="sm" variant="success" onClick={() => setPayFor(p)}>Mark paid</Button>
                  )}
                  {p.status === 'paid' && p.paymentReference && (
                    <span className="small text-muted">{p.paymentMethod} · {p.paymentReference}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pager {...rowsPaged} />
        </>
      )}

      <PayModal
        payable={payFor}
        show={!!payFor}
        onHide={() => setPayFor(null)}
        onPaid={() => { setPayFor(null); load(); }}
      />
    </div>
  );
}
